// ── LEGACY — permission strings from GroupPermissionMapping (DELETE after migration cutover) ──────

/** @deprecated Use NewDamagePermissions after migration */
export enum DamagePermissions {
  CREATE_DAMAGE_INPUT = 'create_damage_input',
  MERGE_DAMAGE_INPUT = 'merge_damage_input',
  CLOSE_DAMAGE_INPUT = 'close_damage_input',
  MERGE_INCIDENTS = 'merge_incidents',
  DAMAGE_REVIEW_SCREEN = 'damage_review_screen',
  EDIT_INCIDENT = 'edit_incident',
  UNMERGE_INCIDENT = 'unmerge_incident',
  SUBMIT_INCIDENT = 'submit_incident',
  EDIT_INCIDENT_BEFORE_APPROVE = 'edit_incident_before_approve',
  EDIT_INCIDENT_AFTER_APPROVE = 'edit_incident_after_approve',
  MANAGE_USER_BRANCHES = 'manage_user_branches',
}

/** @deprecated Use NewTasksPermissions after migration */
export enum TasksPermissions {
  TASK_APPROVE = 'task_approve',
  REASSIGN_TASK = 'reassign_task',
  TASK_REASSIGN_TO_FCS = 'task_reassign_to_fcs',
  TASK_REASSIGN_TO_BRANCH = 'task_reassign_to_branch',
  INCIDENT_CANCEL = 'cancel_incident',
}

/** @deprecated Use NewWorkflowPermissions after migration */
export enum WorkflowPermissions {
  TASK_APPROVE = 'workflow_status_change',
}

/** @deprecated Use NewPhotosPermissions after migration */
export enum PhotosPermissions {
  UPLOAD_PHOTOS = 'upload_photos',
}

/** @deprecated Use NewRppPermissions after migration */
export enum RppPermissions {
  EXPAND_RPP = 'expand_rpp',
}

/** @deprecated Use NewTasksPermissions.REPAIR_TASK_APPROVE after migration */
export enum DamageRecoveryPermissions {
  REPAIR_TASK_APPROVE = 'repair_task_approve',
}

/** @deprecated Use NewWarrantyPermissions after migration */
export enum WarrantyPermissions {
  EXPAND_WARRANTY = 'expand_warranty',
}

/** @deprecated Use NewCommentsPermissions after migration */
export enum CommentsPermissions {
  PUBLIC_COMMENTS = 'public_comments',
  ADMIN_COMMENTS = 'admin_comments',
}

/** @deprecated Use NewClaimPermissions after migration */
export enum CustomerClaimHistoryPermissions {
  CUSTOMER_CLAIM_HISTORY = 'customer_claim_history',
}

/** @deprecated Use AdminPermissions.INCIDENT_HISTORY after migration */
export enum IncidentHistoryPermissions {
  INCIDENT_HISTORY = 'incident_history',
}

// ── NEW PERMISSION SYSTEM — repairhub:* strings, matches backend KnownPermission enum ────────────

export enum NewDamagePermissions {
  DAMAGE_REVIEW_SCREEN         = 'repairhub:incident:review',
  CREATE_DAMAGE_INPUT          = 'repairhub:damage_input:create',
  MERGE_DAMAGE_INPUT           = 'repairhub:damage_input:merge',
  CLOSE_DAMAGE_INPUT           = 'repairhub:damage_input:close',
  MERGE_INCIDENTS              = 'repairhub:incident:merge',
  EDIT_INCIDENT                = 'repairhub:incident:edit',
  UNMERGE_INCIDENT             = 'repairhub:incident:unmerge',
  SUBMIT_INCIDENT              = 'repairhub:incident:submit',
  EDIT_INCIDENT_BEFORE_APPROVE = 'repairhub:incident:edit_before_approve',
  EDIT_INCIDENT_AFTER_APPROVE  = 'repairhub:incident:edit_after_approve',
  VIEW_INCIDENT_HISTORY        = 'repairhub:incident:view_history',
}

export enum NewTasksPermissions {
  TASK_APPROVE             = 'repairhub:task:approve',
  REPAIR_TASK_APPROVE      = 'repairhub:task:repair_approve',
  ADVANCED_REPAIR_DECISION = 'repairhub:task:advanced_decision',
  REASSIGN_TASK            = 'repairhub:task:reassign',
  REASSIGN_TASK_TO_FCS     = 'repairhub:task:reassign_fcs',
  REASSIGN_TASK_TO_BRANCH  = 'repairhub:task:reassign_branch',
  INCIDENT_CANCEL          = 'repairhub:incident:cancel',
}

export enum NewWorkflowPermissions {
  STATUS_CHANGE = 'repairhub:workflow:status_change',
}

export enum NewPhotosPermissions {
  UPLOAD_PHOTOS = 'repairhub:media:upload',
}

export enum NewRppPermissions {
  EXPAND_RPP = 'repairhub:rpp:expand',
}

export enum NewWarrantyPermissions {
  EXPAND_WARRANTY = 'repairhub:warranty:expand',
}

export enum NewCommentsPermissions {
  PUBLIC_COMMENTS = 'repairhub:comment:create_public',
  ADMIN_COMMENTS  = 'repairhub:comment:create_admin',
}

export enum NewClaimPermissions {
  VIEW_CLAIM_HISTORY = 'repairhub:claim:view_history',
}

/** Screen-access permissions — gate page navigation and route entry (see page-navigation-role-visibility-plan.md). */
export enum NewScreenPermissions {
  NEW_SUBMISSIONS  = 'repairhub:screen:new_submissions',
  TASKS            = 'repairhub:screen:tasks',
  IN_PROGRESS      = 'repairhub:screen:in_progress',
  INVOICED         = 'repairhub:screen:invoiced',
  ALL_INCIDENTS    = 'repairhub:screen:all_incidents',
  SUBMIT_NEW       = 'repairhub:screen:submit_new',
  BRANCH_SELECTION = 'repairhub:screen:branch_selection',
  STOLEN           = 'repairhub:screen:stolen',
}

/**
 * Operational screens that stay visible to every recognized legacy user (Legacy All)
 * during parallel run — they carry no repairhub:screen:* strings yet.
 * Intentionally a curated subset (excludes NEW_SUBMISSIONS, SUBMIT_NEW, STOLEN).
 * DELETE after migration cutover.
 */
export const LEGACY_VISIBLE_SCREENS: ReadonlySet<string> = new Set<string>([
  NewScreenPermissions.TASKS,
  NewScreenPermissions.IN_PROGRESS,
  NewScreenPermissions.INVOICED,
  NewScreenPermissions.ALL_INCIDENTS,
  NewScreenPermissions.BRANCH_SELECTION,
]);

/** True when `permission` is an operational screen kept visible for legacy users during parallel run. */
export function isLegacyVisibleScreen(permission: string): boolean {
  return LEGACY_VISIBLE_SCREENS.has(permission);
}

export enum AdminPermissions {
  MANAGE_PERMISSIONS    = 'repairhub:config:manage_permissions',
  ACCESS_CONFIG_PAGES   = 'repairhub:config:access',
  MANAGE_USER_BRANCHES  = 'repairhub:user:manage_branches',
  MANAGE_LOOKUP_OPTIONS = 'repairhub:config:manage_lookup',
  MANAGE_INFO_REQUESTS  = 'repairhub:config:manage_info_requests',
  ADMIN_COMMENTS        = 'repairhub:comment:create_admin',
  INCIDENT_HISTORY      = 'repairhub:incident:view_history',
  MERGE_INCIDENTS       = 'repairhub:incident:merge',
  EXPAND_RPP            = 'repairhub:rpp:expand',
}

// Kept for backward compat — already uses repairhub:* strings
export enum ConfigPermissions {
  MANAGE_PERMISSIONS   = 'repairhub:config:manage_permissions',
  MANAGE_USER_BRANCHES = 'repairhub:user:manage_branches',
}

// ── Union types ───────────────────────────────────────────────────────────────

/** Legacy permission union — for users still on grp_OKTA_DMG_* groups (DELETE after migration cutover) */
export type LegacyPermission =
  | DamagePermissions
  | TasksPermissions
  | PhotosPermissions
  | RppPermissions
  | DamageRecoveryPermissions
  | WarrantyPermissions
  | CommentsPermissions
  | CustomerClaimHistoryPermissions
  | IncidentHistoryPermissions;

/** New permission union — for users on RH_*_Prod groups */
export type NewPermission =
  | NewDamagePermissions
  | NewTasksPermissions
  | NewWorkflowPermissions
  | NewPhotosPermissions
  | NewRppPermissions
  | NewWarrantyPermissions
  | NewCommentsPermissions
  | NewClaimPermissions
  | NewScreenPermissions
  | AdminPermissions;

/** Combined union — accepted by PermissionsService during the parallel-run window */
export type Permission = LegacyPermission | NewPermission | ConfigPermissions;
